/**
 * Правка текстов, которые бот пишет в каналах.
 *
 * Устроено так же, как редактор личных сообщений, но без выключателей:
 * панель без текста не бывает. Пустое поле означает «взять исходный», и
 * кнопка сброса именно это и делает.
 */

import { type ReactNode } from 'react';
import {
  MESSAGES,
  MESSAGE_GROUP_LABEL,
  messageText,
  type MessageGroup,
  type MessageSettings,
} from '@ak/shared';

import { Button, TextArea, TextInput } from './ui.tsx';

const GROUPS: MessageGroup[] = ['panels', 'channels', 'events'];

export function MessageEditor({
  value,
  onChange,
}: {
  value: MessageSettings;
  onChange: (next: MessageSettings) => void;
}): ReactNode {
  return (
    <div className="space-y-6">
      {GROUPS.map((group) => (
        <div key={group}>
          <div
            className="mb-2 text-[0.6875rem] font-semibold tracking-[0.08em] uppercase"
            style={{ color: 'var(--text-muted)' }}
          >
            {MESSAGE_GROUP_LABEL[group]}
          </div>

          <div className="space-y-3">
            {MESSAGES.filter((definition) => definition.group === group).map((definition) => {
              // Показывается всегда готовый текст: пустое поле в форме
              // выглядело бы так, будто бот ничего не пишет.
              const text = messageText(value, definition.id);
              const isDefault = text === definition.defaultText;

              return (
                <div
                  key={definition.id}
                  className="rounded-xl p-4"
                  style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
                >
                  <div className="font-medium">{definition.label}</div>
                  <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                    {definition.description}
                  </p>

                  <div className="mt-3">
                    {definition.multiline ? (
                      <TextArea
                        rows={4}
                        maxLength={definition.maxLength}
                        value={text}
                        onChange={(event) => {
                          onChange({ ...value, [definition.id]: event.target.value });
                        }}
                      />
                    ) : (
                      <TextInput
                        maxLength={definition.maxLength}
                        value={text}
                        onChange={(event) => {
                          onChange({ ...value, [definition.id]: event.target.value });
                        }}
                      />
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                    {definition.placeholders.length > 0 && (
                      <>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          Подстановки:
                        </span>
                        {definition.placeholders.map((placeholder) => (
                          <span
                            key={placeholder.key}
                            title={placeholder.description}
                            className="rounded-md px-1.5 py-0.5 font-mono text-xs"
                            style={{ background: 'var(--surface-panel)', color: 'var(--accent)' }}
                          >
                            {`{${placeholder.key}}`}
                          </span>
                        ))}
                      </>
                    )}

                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {text.length} / {definition.maxLength}
                    </span>

                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isDefault}
                      title={isDefault ? 'Текст и так исходный' : 'Вернуть текст, который был до правок'}
                      onClick={() => {
                        onChange({ ...value, [definition.id]: definition.defaultText });
                      }}
                    >
                      Сбросить текст
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
